import React, { useEffect, useRef, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CFormInput,
  CModal,
  CModalBody,
} from "@coreui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";
import AppLoader from "../../components/seller/AppLoader";
import AppMessage from "../../components/seller/AppMessage";
import { fetchCertificatesList } from "../../features/seller/sellerSlice";

const Certificates = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list: certificate, loading } = useSelector(
    (state) => state.seller.certificates
  );
  const user = useSelector((state) => state.auth.user);

  const [visible, setVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  // console.log("Certificates:", certificate);
  const prevCertificationVerified = useRef(user?.certification_verified);

  useEffect(() => {
    if (!user) return;

    const kycApproved = user.kyc_verified === 1;
    const certApproved = user.certification_verified === 1;

    const prevApproved = prevCertificationVerified.current;

    if (kycApproved && certApproved && prevApproved !== 1) {
      navigate("/seller/profile");
    }

    prevCertificationVerified.current = user.certification_verified;
  }, [user, navigate]);

  useEffect(() => {
    dispatch(fetchCertificatesList());
  }, [dispatch]);

  // const handleView = (fileUrl) => {
  //   setSelectedImage(fileUrl);
  //   setVisible(true);
  // };

  const filtered = Array.isArray(certificate)
    ? certificate.filter((item) =>
        item.document_type.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / entriesPerPage);

  if (loading) return <AppLoader />;

  return (
    <div className="container-fluid px-3">
      <CRow>
        <CCol>
          <h4 className="mb-3">Certificates Status Overview</h4>
        </CCol>
        <CCol className="text-end">
          <CButton
            color="primary"
            className="me-2"
            onClick={() => navigate("/seller/certificate-form")}
          >
            Upload Certificate
          </CButton>
          <CButton color="secondary" onClick={() => navigate(-1)}>
            Back
          </CButton>
        </CCol>
      </CRow>

      {user?.certification_verified === 0 && (
        <AppMessage
          title="Action Required"
          message="Your business certification is pending. Please upload the required certificates for verification."
          style={{ marginTop: "20px" }}
        />
      )}

      <CCard className="mt-3">
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              Show{" "}
              <CFormSelect
                size="sm"
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ display: "inline-block", width: "auto" }}
                options={[
                  { label: "5", value: "5" },
                  { label: "10", value: "10" },
                  { label: "25", value: "25" },
                ]}
              />{" "}
              entries
            </CCol>
            <CCol md={6} className="text-end">
              Search:{" "}
              <CFormInput
                type="text"
                size="sm"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ display: "inline-block", width: "200px" }}
              />
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CTable responsive bordered hover>
            <CTableHead color="light">
              <CTableRow>
                <CTableHeaderCell>#</CTableHeaderCell>
                <CTableHeaderCell>Document Type</CTableHeaderCell>
                <CTableHeaderCell>Uploaded File</CTableHeaderCell>
                <CTableHeaderCell>Verification Status</CTableHeaderCell>
                <CTableHeaderCell>Admin Remark</CTableHeaderCell>
                <CTableHeaderCell>Uploaded At</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {currentData.map((doc, index) => (
                <CTableRow key={doc.id}>
                  <CTableDataCell>{indexOfFirst + index + 1}</CTableDataCell>
                  <CTableDataCell>{doc.document_type || "-"}</CTableDataCell>
                  <CTableDataCell>
                    {doc.document_path ? (
                      <CButton
                        size="sm"
                        color="info"
                        onClick={() =>
                          window.open(
                            `https://scraptrade.com.au/storage/${doc.document_path}`,
                            "_blank"
                          )
                        }
                      >
                        View
                      </CButton>
                    ) : (
                      "-"
                    )}
                  </CTableDataCell>
                  <CTableDataCell>
                    <span
                      className={`badge bg-${
                        doc.status === "approved" ? "success" : "warning"
                      }`}
                    >
                      {doc.status ? capitalizeFirst(doc.status) : "-"}
                    </span>
                  </CTableDataCell>
                  <CTableDataCell>{doc.admin_remark || "_"}</CTableDataCell>
                  <CTableDataCell>
                    {doc.created_at ? timeAgo(doc.created_at) : "-"}
                  </CTableDataCell>
                </CTableRow>
              ))}
              {currentData.length === 0 && (
                <CTableRow>
                  <CTableDataCell
                    colSpan="6"
                    className="text-center text-muted"
                  >
                    No Certificates records found.
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>

          {/* Pagination Controls */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Showing {indexOfFirst + 1} to{" "}
              {Math.min(indexOfLast, filtered.length)} of {filtered.length}{" "}
              entries
            </div>
            <div className="d-flex align-items-center">
              <CButton
                size="sm"
                color="light"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="me-2"
              >
                Prev
              </CButton>

              {[...Array(totalPages)].map((_, i) => (
                <CButton
                  key={i}
                  size="sm"
                  color={currentPage === i + 1 ? "primary" : "light"}
                  onClick={() => setCurrentPage(i + 1)}
                  className="me-2"
                >
                  {i + 1}
                </CButton>
              ))}

              <CButton
                size="sm"
                color="light"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </CButton>
            </div>
          </div>
        </CCardBody>
      </CCard>

      {/* Image Modal */}
      <CModal visible={visible} onClose={() => setVisible(false)} size="lg">
        <CModalBody>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="KYC Document"
              style={{ width: "100%", height: "auto" }}
            />
          )}
        </CModalBody>
      </CModal>
    </div>
  );
};

export default Certificates;
